import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  MessageSquare, Send, ArrowLeft, User, Building2, 
  Search, Clock, Check, CheckCheck, Loader2, Plus,
  Briefcase, MoreVertical
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get stored auth data
const getAuthData = () => {
  try {
    const token = localStorage.getItem('sfretire_token');
    const user = JSON.parse(localStorage.getItem('sfretire_user') || 'null');
    return { token, user };
  } catch {
    return { token: null, user: null };
  }
};

// Format time for messages
const formatMessageTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Hier ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays < 7) {
    return date.toLocaleDateString('fr-FR', { weekday: 'short' }) + ' ' + 
           date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } else {
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  }
};

// Conversation List Item
const ConversationItem = ({ conversation, isActive, onClick, currentUserType }) => {
  const unreadCount = currentUserType === 'retraite' 
    ? conversation.unread_retraite 
    : conversation.unread_entreprise;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      onClick={onClick}
      className={`p-4 cursor-pointer border-b border-border transition-colors ${
        isActive 
          ? 'bg-primary/10 border-l-4 border-l-primary' 
          : 'hover:bg-secondary/50'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          currentUserType === 'retraite' ? 'bg-blue-100' : 'bg-green-100'
        }`}>
          {currentUserType === 'retraite' ? (
            <Building2 size={20} className="text-blue-600" />
          ) : (
            <User size={20} className="text-green-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className={`font-medium truncate ${unreadCount > 0 ? 'text-foreground' : 'text-foreground/80'}`}>
              {conversation.other_party?.name || 'Contact'}
            </h4>
            {conversation.last_message_at && (
              <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                {formatMessageTime(conversation.last_message_at)}
              </span>
            )}
          </div>
          {conversation.mission_title && (
            <p className="text-xs text-primary flex items-center gap-1 mt-0.5">
              <Briefcase size={10} />
              {conversation.mission_title}
            </p>
          )}
          <p className={`text-sm truncate mt-1 ${
            unreadCount > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'
          }`}>
            {conversation.last_message_preview || 'Nouvelle conversation'}
          </p>
        </div>
        {unreadCount > 0 && (
          <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0">
            {unreadCount}
          </span>
        )}
      </div>
    </motion.div>
  );
};

// Single Message Component
const MessageBubble = ({ message, isOwn, showAvatar }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex items-end gap-2 mb-3 ${isOwn ? 'flex-row-reverse' : ''}`}
    >
      {showAvatar && !isOwn && (
        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
          {message.sender_type === 'entreprise' ? (
            <Building2 size={16} className="text-muted-foreground" />
          ) : (
            <User size={16} className="text-muted-foreground" />
          )}
        </div>
      )}
      {!showAvatar && !isOwn && <div className="w-8 flex-shrink-0" />}
      
      <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
        <div className={`px-4 py-2.5 rounded-2xl ${
          isOwn 
            ? 'bg-primary text-primary-foreground rounded-br-md' 
            : 'bg-secondary rounded-bl-md'
        }`}>
          <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
        </div>
        <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
          <span className="text-[10px] text-muted-foreground">
            {formatMessageTime(message.created_at)}
          </span>
          {isOwn && (
            message.read ? (
              <CheckCheck size={12} className="text-primary" />
            ) : (
              <Check size={12} className="text-muted-foreground" />
            )
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Chat Area Component
const ChatArea = ({ 
  conversation, 
  messages, 
  onSendMessage, 
  currentUserType, 
  currentUserId,
  loading,
  onLoadMore,
  hasMore
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    const success = await onSendMessage(newMessage.trim());
    if (success) {
      setNewMessage('');
    }
    setSending(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center bg-secondary/20">
        <div className="text-center">
          <MessageSquare size={48} className="mx-auto text-muted-foreground opacity-50 mb-4" />
          <h3 className="text-lg font-medium text-foreground">Vos messages</h3>
          <p className="text-muted-foreground mt-1">
            Sélectionnez une conversation pour commencer
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-background">
      {/* Chat Header */}
      <div className="h-16 px-4 flex items-center justify-between border-b border-border bg-card">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            currentUserType === 'retraite' ? 'bg-blue-100' : 'bg-green-100'
          }`}>
            {currentUserType === 'retraite' ? (
              <Building2 size={20} className="text-blue-600" />
            ) : (
              <User size={20} className="text-green-600" />
            )}
          </div>
          <div>
            <h3 className="font-medium text-foreground">
              {conversation.other_party?.name || 'Contact'}
            </h3>
            {conversation.mission_title && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Briefcase size={10} />
                {conversation.mission_title}
              </p>
            )}
          </div>
        </div>
        <Button variant="ghost" size="icon">
          <MoreVertical size={20} />
        </Button>
      </div>

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4"
      >
        {loading && (
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}
        
        {hasMore && !loading && (
          <div className="text-center py-2">
            <Button variant="ghost" size="sm" onClick={onLoadMore}>
              Charger les messages précédents
            </Button>
          </div>
        )}

        {messages.map((message, index) => {
          const isOwn = message.sender_type === currentUserType;
          const showAvatar = index === 0 || 
            messages[index - 1].sender_type !== message.sender_type;
          
          return (
            <MessageBubble 
              key={message.id} 
              message={message} 
              isOwn={isOwn}
              showAvatar={showAvatar}
            />
          );
        })}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={handleSend} className="p-4 border-t border-border bg-card">
        <div className="flex items-center gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Écrivez votre message..."
            className="flex-1"
            disabled={sending}
            data-testid="message-input"
          />
          <Button 
            type="submit" 
            variant="gold" 
            size="icon"
            disabled={!newMessage.trim() || sending}
            data-testid="send-message-btn"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send size={20} />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Main Messaging Page
const MessagingPage = () => {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileList, setShowMobileList] = useState(true);
  
  const { token, user } = getAuthData();
  const userType = user?.role || user?.user_type || 'retraite';
  const userProfileId = user?.profile?.id;

  // Polling for new messages
  const pollIntervalRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setConversations(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [token]);

  const fetchMessages = useCallback(async (convId, append = false) => {
    if (!token || !convId) return;
    
    setMessagesLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/conversations/${convId}/messages?limit=50`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        if (append) {
          setMessages(prev => [...(data.items || []), ...prev]);
        } else {
          setMessages(data.items || []);
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  }, [token]);

  const markAsRead = useCallback(async (convId) => {
    if (!token || !convId) return;
    
    try {
      await fetch(
        `${API_URL}/api/conversations/${convId}/read?user_type=${userType}`,
        { 
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` } 
        }
      );
      
      // Update local state
      setConversations(prev => prev.map(c => {
        if (c.id === convId) {
          return {
            ...c,
            [userType === 'retraite' ? 'unread_retraite' : 'unread_entreprise']: 0
          };
        }
        return c;
      }));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  }, [token, userType]);

  // Initial load
  useEffect(() => {
    if (!token || !user) {
      navigate('/');
      return;
    }

    const loadData = async () => {
      setLoading(true);
      await fetchConversations();
      setLoading(false);
    };

    loadData();
  }, [token, user, navigate, fetchConversations]);

  // Handle conversation selection
  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === conversationId);
      if (conv) {
        setActiveConversation(conv);
        fetchMessages(conversationId);
        markAsRead(conversationId);
        setShowMobileList(false);
      }
    }
  }, [conversationId, conversations, fetchMessages, markAsRead]);

  // Polling for new messages
  useEffect(() => {
    if (activeConversation) {
      pollIntervalRef.current = setInterval(() => {
        fetchMessages(activeConversation.id);
        fetchConversations();
      }, 5000); // Poll every 5 seconds
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [activeConversation, fetchMessages, fetchConversations]);

  const handleSelectConversation = (conversation) => {
    setActiveConversation(conversation);
    navigate(`/messages/${conversation.id}`);
    fetchMessages(conversation.id);
    markAsRead(conversation.id);
    setShowMobileList(false);
  };

  const handleSendMessage = async (content) => {
    if (!activeConversation || !userProfileId) return false;

    try {
      const res = await fetch(
        `${API_URL}/api/conversations/${activeConversation.id}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            content,
            sender_id: userProfileId,
            sender_type: userType
          })
        }
      );

      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, data.message]);
        
        // Update conversation preview
        setConversations(prev => prev.map(c => {
          if (c.id === activeConversation.id) {
            return {
              ...c,
              last_message_at: new Date().toISOString(),
              last_message_preview: content.slice(0, 100)
            };
          }
          return c;
        }));
        
        return true;
      } else {
        toast.error('Erreur lors de l\'envoi du message');
        return false;
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur de connexion');
      return false;
    }
  };

  // Filter conversations
  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const name = conv.other_party?.name || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-20 pb-0 flex flex-col">
        <div className="flex-1 flex overflow-hidden">
          {/* Conversations List - Left Panel */}
          <div className={`w-full md:w-80 lg:w-96 border-r border-border bg-card flex-shrink-0 flex flex-col ${
            !showMobileList && activeConversation ? 'hidden md:flex' : 'flex'
          }`}>
            {/* List Header */}
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <MessageSquare size={20} className="text-primary" />
                Messages
              </h2>
              <div className="mt-3 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher..."
                  className="pl-9"
                />
              </div>
            </div>

            {/* Conversations */}
            <div className="flex-1 overflow-y-auto">
              {filteredConversations.length === 0 ? (
                <div className="p-6 text-center">
                  <MessageSquare className="mx-auto mb-3 text-muted-foreground opacity-50" size={32} />
                  <p className="text-muted-foreground">
                    {searchQuery ? 'Aucune conversation trouvée' : 'Aucune conversation'}
                  </p>
                </div>
              ) : (
                filteredConversations.map(conv => (
                  <ConversationItem
                    key={conv.id}
                    conversation={conv}
                    isActive={activeConversation?.id === conv.id}
                    onClick={() => handleSelectConversation(conv)}
                    currentUserType={userType}
                  />
                ))
              )}
            </div>
          </div>

          {/* Chat Area - Right Panel */}
          <div className={`flex-1 flex flex-col ${
            showMobileList && !activeConversation ? 'hidden md:flex' : 'flex'
          }`}>
            {/* Mobile back button */}
            {activeConversation && (
              <div className="md:hidden p-2 border-b border-border">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowMobileList(true);
                    navigate('/messages');
                  }}
                >
                  <ArrowLeft size={16} className="mr-2" />
                  Retour
                </Button>
              </div>
            )}
            
            <ChatArea
              conversation={activeConversation}
              messages={messages}
              onSendMessage={handleSendMessage}
              currentUserType={userType}
              currentUserId={userProfileId}
              loading={messagesLoading}
              onLoadMore={() => {}}
              hasMore={false}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default MessagingPage;
